import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useCompanyDeviceSettings } from '../hooks/useCompanyDeviceSettings';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import {
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  ArrowLeftIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface Device {
  id: string;
  name: string;
  type: string;
  hardware_id?: string;
  status?: string;
  created_at: string;
  updated_at?: string;
  last_seen?: string;
  last_seen_from?: string;
  last_seen_from_ip?: string;
  system_info?: Record<string, any>;
  metadata?: Record<string, any>;
  device_hash?: string;
  last_activated?: string;
}

const DeviceDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation('devices');
  const { settings: deviceSettings } = useCompanyDeviceSettings();
  
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);

  usePageTitle(t('details.title'), t('details.subtitle'));

  // Try to get device from devices list cache first (optimization)
  // Check all possible query keys for devices list
  const queryCache = queryClient.getQueryCache();
  const allDevicesQueries = queryCache.findAll({ queryKey: ['devices'] });
  let cachedDevice: Device | undefined;
  
  // Search through all devices queries to find the device
  for (const query of allDevicesQueries) {
    const devices = query.state.data as Device[] | undefined;
    if (devices && Array.isArray(devices)) {
      const found = devices.find(d => d.id === id);
      if (found) {
        cachedDevice = found;
        break;
      }
    }
  }

  // Fetch device details
  const { data: device, isLoading, error } = useQuery<Device>({
    queryKey: ['device-details', id],
    queryFn: async () => {
      try {
        const response = await apiService.get(`/devices/${id}/details`);
        return response.data;
      } catch (err: any) {
        // If API fails but we have cached data, use it as fallback
        if (cachedDevice) {
          console.warn('Failed to fetch device details from API, using cached data:', err);
          return cachedDevice;
        }
        // Re-throw if no cached data available
        throw err;
      }
    },
    enabled: !!id,
    // Use cached device as initial data if available
    initialData: cachedDevice,
    // Retry once on error
    retry: 1,
  });

  const isAdmin = user?.role === 'admin' || user?.role === 'sysadmin';
  // For regular users, check company setting. For admins, always allow (subject to backend checks)
  const canRevoke = isAdmin || (deviceSettings?.allow_revoke_device !== false && device?.status !== 'revoked');

  // Get device type icon
  const getDeviceIcon = (type?: string) => {
    switch (type) {
      case 'mobile':
        return DevicePhoneMobileIcon;
      case 'desktop':
      case 'server':
      case 'cli':
        return ComputerDesktopIcon;
      default:
        return ComputerDesktopIcon;
    }
  };

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  // Handle revoke
  const handleRevoke = async () => {
    if (!device || !id) return;

    setIsRevoking(true);
    try {
      await apiService.deleteDevice(id);
      toast.success(t('details.deviceRevokedSuccess'));
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['device-details', id] });
      navigate('/devices');
    } catch (err: any) {
      toast.error(err.response?.data?.error || t('details.failedToRevokeDevice'));
    } finally {
      setIsRevoking(false);
      setShowRevokeModal(false);
    }
  };


  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="card animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (error || !device) {
    return (
      <div className="card text-center py-12">
        <p className="text-red-600">{t('details.failedToLoadDevice')}</p>
        <button
          onClick={() => navigate('/devices')}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          {t('details.backToDevices')}
        </button>
      </div>
    );
  }

  const DeviceIcon = getDeviceIcon(device.type);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/devices')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title={t('details.backToDevicesTitle')}
          >
            <ArrowLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
          <div className="flex items-center space-x-3">
            <DeviceIcon className="w-8 h-8 text-gray-600 dark:text-gray-400" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{device.name}</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                {device.type} {t('details.deviceTypeSuffix')}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            disabled
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md cursor-not-allowed opacity-60"
            title="Coming soon"
          >
            <KeyIcon className="w-4 h-4 mr-2" />
            {t('details.backupPublicKey')} <span className="ml-1 text-xs">{t('details.soon')}</span>
          </button>
          {canRevoke && (
            <button
              onClick={() => setShowRevokeModal(true)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
            >
              <TrashIcon className="w-4 h-4 mr-2" />
              {t('details.revokeDevice')}
            </button>
          )}
        </div>
      </div>

      {/* Device Information */}
      <div className="card p-6 space-y-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('details.deviceInformation')}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('details.deviceName')}</label>
            <p className="mt-1 text-sm text-gray-900 dark:text-white">{device.name}</p>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('details.deviceType')}</label>
            <p className="mt-1 text-sm text-gray-900 dark:text-white capitalize">{device.type}</p>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('details.status')}</label>
            <p className="mt-1">
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  device.status === 'active'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : device.status === 'revoked'
                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}
              >
                {device.status || 'active'}
              </span>
            </p>
          </div>
          
          {device.hardware_id && (
            <div>
              <label className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('details.hardwareId')}</label>
              <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{device.hardware_id}</p>
            </div>
          )}
          
          {device.device_hash && (
            <div>
              <label className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('details.deviceHash')}</label>
              <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono break-all">{device.device_hash}</p>
            </div>
          )}
          
          <div>
            <label className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('details.createdAt')}</label>
            <p className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(device.created_at)}</p>
          </div>
          
          {device.updated_at && (
            <div>
              <label className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('details.lastUpdated')}</label>
              <p className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(device.updated_at)}</p>
            </div>
          )}
          
          {device.last_seen && (
            <div>
              <label className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('details.lastSeen')}</label>
              <p className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(device.last_seen)}</p>
            </div>
          )}
          
          {device.last_seen_from_ip && (
            <div>
              <label className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('details.lastIPAddress')}</label>
              <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{device.last_seen_from_ip}</p>
            </div>
          )}
          
          {device.last_activated && (
            <div>
              <label className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('details.lastActivated')}</label>
              <p className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(device.last_activated)}</p>
            </div>
          )}
        </div>

        {/* System Information */}
        {device.system_info && Object.keys(device.system_info).length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">{t('details.systemInformation')}</h3>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                {JSON.stringify(device.system_info, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* Metadata */}
        {device.metadata && Object.keys(device.metadata).length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">{t('details.metadata')}</h3>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                {JSON.stringify(device.metadata, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* Revoke Confirmation Modal */}
      {showRevokeModal && createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
              onClick={() => !isRevoking && setShowRevokeModal(false)}
            ></div>

            {/* Modal */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                      {t('details.revokeConfirmTitle')}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('details.revokeConfirmMessage', { name: device.name })}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                <button
                  type="button"
                  onClick={handleRevoke}
                  disabled={isRevoking}
                  className="w-full inline-flex justify-center items-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {isRevoking ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      {t('details.revoking')}
                    </>
                  ) : (
                    t('details.revokeDevice')
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowRevokeModal(false)}
                  disabled={isRevoking}
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-governate-500 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto sm:text-sm"
                >
                  {t('details.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DeviceDetails;
